/**
 * DomainSelect (DC-1493)
 *
 * Shared dropdown for assigning newly uploaded artifacts (datasets, interviews,
 * governance documents) to a triangulation domain at creation time. Wired into
 * three upload surfaces:
 *   - JourneyCreationForm
 *   - OrganizationalInterviews upload
 *   - GovernanceDocuments upload
 *
 * Fetches GET /api/triangulation/domains once on mount via the useApiClient
 * cached layer. The destructured stable-ref pattern matches the convention
 * locked in DC-1492 — calling `useApiClient()` directly inside a useCallback
 * dependency array causes the jsdom infinite-render bug captured in the
 * Sprint 16 SESSION-LOG.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import type { DomainSummary } from '../../types/triangulation';

interface DomainSelectProps {
  value: string | null;
  onChange: (domainId: string | null) => void;
  /** When true, the "— No domain —" option is suppressed. Defaults to false. */
  required?: boolean;
  /** Visible field label. Defaults to "Domain". */
  label?: string;
  /** Helper copy below the field. Defaults to a generic optional-assignment line. */
  helpText?: string;
  /** Forwarded to the wrapper div for layout integration. */
  className?: string;
  /** Forwarded to the underlying select element. */
  id?: string;
  /**
   * Force the control into a disabled (read-only) state regardless of load
   * status — e.g. a non-captain viewing the journey domain picker (DC-1617).
   * Defaults to false. The internal loading/empty/error disabling still applies.
   */
  disabled?: boolean;
}

const DEFAULT_HELP_TEXT =
  'Assign this artifact to an organizational data domain (optional).';

export const DomainSelect: React.FC<DomainSelectProps> = ({
  value,
  onChange,
  required = false,
  label = 'Domain',
  helpText = DEFAULT_HELP_TEXT,
  className,
  id,
  disabled = false,
}) => {
  // Stable refs — see comment above.
  const { get } = useApiClient();

  const [domains, setDomains] = useState<DomainSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await get<DomainSummary[]>('/api/triangulation/domains');
      setDomains(Array.isArray(result) ? result : []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadDomains();
  }, [loadDomains]);

  const selectId = id ?? 'domain-select';
  const isEmpty = !loading && !error && domains.length === 0;

  return (
    <div className={className} data-testid="domain-select">
      <label
        htmlFor={selectId}
        style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--text, #e8eefc)',
          marginBottom: '0.25rem',
        }}
      >
        {label}
        {!required && (
          <span style={{ color: 'var(--muted, #8ea1c7)', fontWeight: 400, marginLeft: 6 }}>
            (optional)
          </span>
        )}
      </label>

      <select
        id={selectId}
        data-testid="domain-select-input"
        value={value ?? ''}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === '' ? null : next);
        }}
        disabled={disabled || loading || isEmpty || Boolean(error)}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid var(--line, #1c2a54)',
          borderRadius: '0.375rem',
          background: 'var(--pane, #0f1730)',
          color: 'var(--text, #e8eefc)',
          appearance: 'auto',
          // Render the native option-list popup dark to match the app theme.
          colorScheme: 'dark',
        }}
      >
        {loading && <option value="">Loading domains...</option>}
        {!loading && error && <option value="">Could not load domains</option>}
        {/* DC-1645: Domains moved from Governance to Settings */}
        {!loading && !error && isEmpty && (
          <option value="">No domains yet — create one in Settings → Domains</option>
        )}
        {!loading && !error && !isEmpty && (
          <>
            {/* DC-1619 — in required mode, show a disabled placeholder instead
                of "— No domain —" so the user must make an explicit pick (no
                default lands an artifact in the wrong domain by inertia). */}
            {required && (
              <option value="" disabled>
                — Select a domain —
              </option>
            )}
            {!required && <option value="">— No domain —</option>}
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.slug})
              </option>
            ))}
          </>
        )}
      </select>

      <p
        style={{
          margin: '0.25rem 0 0 0',
          fontSize: '0.75rem',
          color: error ? 'var(--error, #e74c3c)' : 'var(--muted, #8ea1c7)',
        }}
      >
        {error ? `Domain list unavailable — ${error}` : helpText}
      </p>
    </div>
  );
};

export default DomainSelect;
